import type { Achievement } from "@obean/game-contract";

/**
 * Achievements (ADR 0043, P5-LOOP-03): each game's own (its manifest's `achievements`) and a few
 * across Obean, earned once and kept. What they count is kept here per person in this browser, as
 * beans and solo progress are: each stat added up over every play, the missions won (with their
 * best stars), the games played and the plays with friends. Solo and rooms count the same.
 */
export interface Feats {
  /** Per game, each outcome stat's total over every play. */
  sums: Record<string, Record<string, number>>;
  /** Per game, the missions won and their best stars. */
  won: Record<string, Record<string, number>>;
  /** The games played at least once. */
  games: string[];
  /** Plays in a room with friends. */
  together: number;
  /** What's been earned ("beantumble:row-gardener", "obean:explorer") and on which UTC day. */
  earned: Record<string, string>;
  /** Signed in on their own browser: the guest's record has joined theirs (once). */
  guestIn: boolean;
}

export const emptyFeats = (): Feats => ({
  sums: {},
  won: {},
  games: [],
  together: 0,
  earned: {},
  guestIn: false,
});

/** One finished play, as the result sees it. */
export interface FeatPlay {
  gameId: string;
  missionId: string;
  won: boolean;
  stats: Record<string, number>;
  /** In a room with at least one friend. */
  together: boolean;
}

/** An achievement as shown: its key, name and what it asks. */
export interface Feat {
  key: string;
  name: string;
  about: string;
}

/** Across Obean: what any game counts towards. */
interface ObeanFeat extends Omit<Feat, "key"> {
  id: string;
  /** How far, and how many it takes. */
  progress: (f: Feats, ctx: FeatContext) => { have: number; at: number };
}

export interface FeatContext {
  /** The level beans have reached, after this play. */
  level: number;
  /** The live games, to count "every game" against. */
  games: readonly string[];
}

export const OBEAN_FEATS: readonly ObeanFeat[] = [
  {
    id: "explorer",
    name: "Explorer",
    about: "Play three different games",
    progress: (f) => ({ have: f.games.length, at: 3 }),
  },
  {
    id: "all-rounder",
    name: "All-rounder",
    about: "Play every game",
    progress: (f, ctx) => ({
      have: ctx.games.filter((g) => f.games.includes(g)).length,
      at: Math.max(1, ctx.games.length),
    }),
  },
  {
    id: "pod-people",
    name: "Pod people",
    about: "Play five times with friends in a room",
    progress: (f) => ({ have: f.together, at: 5 }),
  },
  {
    id: "growing",
    name: "Growing",
    about: "Reach level 5",
    progress: (_f, ctx) => ({ have: ctx.level, at: 5 }),
  },
];

/** How far a game's achievement has got. */
export function progressOf(f: Feats, gameId: string, a: Achievement): { have: number; at: number } {
  const won = f.won[gameId] ?? {};
  const have =
    a.kind === "sum"
      ? (f.sums[gameId]?.[a.stat ?? ""] ?? 0)
      : a.kind === "won"
        ? Object.keys(won).length
        : Object.values(won).reduce((n, s) => n + s, 0);
  return { have, at: a.at };
}

/** The record after a play, and what it earned just now (none twice). */
export function recordFeats(
  before: Feats,
  play: FeatPlay,
  defs: readonly Achievement[],
  ctx: FeatContext,
  day: string,
): { feats: Feats; earned: Feat[] } {
  const sums = { ...before.sums, [play.gameId]: { ...before.sums[play.gameId] } };
  const mine = sums[play.gameId] ?? {};
  for (const [stat, n] of Object.entries(play.stats))
    if (Number.isFinite(n) && n > 0) mine[stat] = (mine[stat] ?? 0) + Math.round(n);
  const won = { ...before.won, [play.gameId]: { ...before.won[play.gameId] } };
  if (play.won) {
    const missions = won[play.gameId] ?? {};
    const stars = Math.min(3, Math.max(0, Math.round(play.stats["stars"] ?? 0)));
    missions[play.missionId] = Math.max(missions[play.missionId] ?? 0, stars);
  }
  const feats: Feats = {
    ...before,
    sums,
    won,
    games: before.games.includes(play.gameId) ? before.games : [...before.games, play.gameId],
    together: before.together + (play.together ? 1 : 0),
    earned: { ...before.earned },
  };
  const earned: Feat[] = [];
  const earn = (key: string, name: string, about: string, p: { have: number; at: number }) => {
    if (feats.earned[key] !== undefined || p.have < p.at) return;
    feats.earned[key] = day;
    earned.push({ key, name, about });
  };
  for (const a of defs)
    earn(`${play.gameId}:${a.id}`, a.name, a.about, progressOf(feats, play.gameId, a));
  for (const o of OBEAN_FEATS) earn(`obean:${o.id}`, o.name, o.about, o.progress(feats, ctx));
  return { feats, earned };
}

/**
 * A browser's guest record joining a person's on their own browser (ADR 0029): different plays,
 * so the totals add up; missions keep their best stars; earned keeps the earliest day.
 */
export function joinFeats(person: Feats, guest: Feats): Feats {
  const sums: Feats["sums"] = { ...person.sums };
  for (const [g, stats] of Object.entries(guest.sums)) {
    const into = { ...sums[g] };
    for (const [s, n] of Object.entries(stats)) into[s] = (into[s] ?? 0) + n;
    sums[g] = into;
  }
  const won: Feats["won"] = { ...person.won };
  for (const [g, missions] of Object.entries(guest.won)) {
    const into = { ...won[g] };
    for (const [m, s] of Object.entries(missions)) into[m] = Math.max(into[m] ?? 0, s);
    won[g] = into;
  }
  const earned = { ...person.earned };
  for (const [k, day] of Object.entries(guest.earned))
    if (earned[k] === undefined || day < (earned[k] ?? "")) earned[k] = day;
  return {
    sums,
    won,
    games: [...new Set([...person.games, ...guest.games])],
    together: person.together + guest.together,
    earned,
    guestIn: true,
  };
}

const KEY = (profile: string | null) =>
  profile === null ? "obean.feats" : `obean.feats.${profile}`;

const record = <T>(v: unknown, ok: (x: unknown) => x is T): Record<string, T> =>
  v !== null && typeof v === "object"
    ? Object.fromEntries(Object.entries(v).filter((e): e is [string, T] => ok(e[1])))
    : {};
const isCount = (x: unknown): x is number => typeof x === "number" && x >= 0 && Number.isFinite(x);
const isDay = (x: unknown): x is string => typeof x === "string" && /^\d{4}-\d{2}-\d{2}$/.test(x);
const isCounts = (x: unknown): x is Record<string, number> =>
  x !== null && typeof x === "object" && Object.values(x).every(isCount);

/** This browser's record for the guest, or a signed-in person; a damaged one reads as empty. */
export function loadFeats(
  profile: string | null = null,
  storage: Pick<Storage, "getItem"> | null = safeStorage(),
): Feats {
  try {
    const raw = storage?.getItem(KEY(profile));
    if (!raw) return emptyFeats();
    const f = JSON.parse(raw) as Partial<Feats>;
    return {
      sums: record(f.sums, isCounts),
      won: record(f.won, isCounts),
      games: Array.isArray(f.games) ? f.games.filter((g) => typeof g === "string") : [],
      together: isCount(f.together) ? Math.floor(f.together) : 0,
      earned: record(f.earned, isDay),
      guestIn: f.guestIn === true,
    };
  } catch {
    return emptyFeats();
  }
}

export function saveFeats(
  feats: Feats,
  profile: string | null = null,
  storage: Pick<Storage, "setItem"> | null = safeStorage(),
): void {
  try {
    storage?.setItem(KEY(profile), JSON.stringify(feats));
  } catch {
    // Private windows and full storage: achievements just aren't kept.
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === "undefined" ? null : localStorage;
  } catch {
    return null;
  }
}
